import React from 'react';
import { Cookie, Settings, Shield, BarChart3, Megaphone } from 'lucide-react';
import { PageHero } from '../components/shared/PageHero';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { useCookie } from '../contexts/CookieContext';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

export default function CookiePolicy() {
  const { openSettings } = useCookie();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <PageHero
        title="Política de Cookies"
        subtitle="Información sobre el uso de cookies en nuestro sitio web"
        tag="Legal"
      />

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container-max section-padding">
          <div className="max-w-4xl mx-auto">
            {/* 最后更新日期 */}
            <AnimatedSection>
              <div className="mb-8 text-sm text-zwheel-gray-500">
                Última actualización: {new Date().toLocaleDateString('es-ES', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
            </AnimatedSection>

            {/* 简介 */}
            <AnimatedSection>
              <div className="blog-content-clean mb-12">
                <p className="text-lg text-zwheel-gray-700 leading-relaxed mb-6">
                  En ZWHEEL, respetamos su privacidad y nos comprometemos a ser transparentes sobre cómo utilizamos las cookies 
                  y tecnologías similares en nuestro sitio web. Esta Política de Cookies explica qué son las cookies, cómo las 
                  utilizamos y cómo puede gestionar sus preferencias.
                </p>
              </div>
            </AnimatedSection>

            {/* 什么是Cookies */}
            <AnimatedSection delay={0.1}>
              <div className="blog-content-clean mb-12">
                <h2 className="flex items-center space-x-3 mb-4">
                  <Cookie className="h-8 w-8 text-zwheel-black" />
                  <span>¿Qué son las Cookies?</span>
                </h2>
                <p>
                  Las cookies son pequeños archivos de texto que se almacenan en su dispositivo (ordenador, tablet o móvil) 
                  cuando visita un sitio web. Las cookies permiten que el sitio web recuerde sus acciones y preferencias durante 
                  un período de tiempo, por lo que no tiene que volver a configurarlas cada vez que regrese al sitio o navegue 
                  de una página a otra.
                </p>
                <p>
                  Las cookies pueden ser "persistentes" o "de sesión". Las cookies persistentes permanecen en su dispositivo 
                  durante un período determinado o hasta que las elimine, mientras que las cookies de sesión se eliminan 
                  automáticamente cuando cierra su navegador.
                </p>
              </div>
            </AnimatedSection>

            {/* 我们使用的Cookies类型 */}
            <AnimatedSection delay={0.2}>
              <div className="blog-content-clean mb-12">
                <h2 className="flex items-center space-x-3 mb-6">
                  <Settings className="h-8 w-8 text-zwheel-black" />
                  <span>Tipos de Cookies que Utilizamos</span>
                </h2>
                
                <div className="space-y-8">
                  {/* Cookies Necesarias */}
                  <div className="p-6 bg-zwheel-gray-50 rounded-xl border-2 border-zwheel-black">
                    <div className="flex items-center space-x-3 mb-4">
                      <Shield className="h-6 w-6 text-zwheel-black" />
                      <h3 className="text-xl font-bold text-zwheel-black">Cookies Necesarias</h3>
                      <span className="ml-auto text-xs px-3 py-1 bg-zwheel-black text-white rounded-full">
                        Siempre activas
                      </span>
                    </div>
                    <p className="mb-3">
                      Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar. 
                      Nos permiten recordar sus preferencias básicas y proporcionar funciones fundamentales como:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Mantener su sesión activa mientras navega por el sitio</li>
                      <li>Recordar sus preferencias de cookies</li>
                      <li>Garantizar la seguridad del sitio web</li>
                      <li>Permitir el funcionamiento de funciones básicas como la navegación</li>
                    </ul>
                    <p className="mt-4 text-sm text-zwheel-gray-600">
                      <strong>Duración:</strong> Estas cookies se eliminan cuando cierra su navegador (cookies de sesión) 
                      o después de un período determinado (cookies persistentes).
                    </p>
                  </div>

                  {/* Cookies de Análisis */}
                  <div className="p-6 bg-white rounded-xl border-2 border-gray-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <BarChart3 className="h-6 w-6 text-zwheel-black" />
                      <h3 className="text-xl font-bold text-zwheel-black">Cookies de Análisis</h3>
                    </div>
                    <p className="mb-3">
                      Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web recopilando 
                      información de forma anónima. Utilizamos esta información para:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Analizar el tráfico del sitio web y los patrones de uso</li>
                      <li>Mejorar la funcionalidad y el rendimiento del sitio</li>
                      <li>Identificar problemas técnicos y áreas de mejora</li>
                      <li>Comprender qué páginas son más populares</li>
                    </ul>
                    <p className="mt-4 text-sm text-zwheel-gray-600">
                      <strong>Duración:</strong> Estas cookies pueden permanecer en su dispositivo hasta 2 años, 
                      o hasta que las elimine manualmente.
                    </p>
                    <p className="mt-2 text-sm text-zwheel-gray-600">
                      <strong>Proveedores:</strong> Utilizamos servicios de análisis como Google Analytics para recopilar 
                      esta información de forma agregada y anónima.
                    </p>
                  </div>

                  {/* Cookies de Marketing */}
                  <div className="p-6 bg-white rounded-xl border-2 border-gray-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <Megaphone className="h-6 w-6 text-zwheel-black" />
                      <h3 className="text-xl font-bold text-zwheel-black">Cookies de Marketing</h3>
                    </div>
                    <p className="mb-3">
                      Estas cookies se utilizan para mostrarle anuncios relevantes y medir la efectividad de nuestras 
                      campañas publicitarias. Estas cookies pueden:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                      <li>Rastrear su actividad en diferentes sitios web</li>
                      <li>Crear un perfil de sus intereses</li>
                      <li>Mostrarle anuncios personalizados basados en sus preferencias</li>
                      <li>Medir la efectividad de nuestras campañas publicitarias</li>
                    </ul>
                    <p className="mt-4 text-sm text-zwheel-gray-600">
                      <strong>Duración:</strong> Estas cookies pueden permanecer en su dispositivo hasta 1 año, 
                      o hasta que las elimine manualmente.
                    </p>
                    <p className="mt-2 text-sm text-zwheel-gray-600">
                      <strong>Nota:</strong> Solo utilizamos cookies de marketing si usted ha dado su consentimiento explícito.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* 如何管理Cookies */}
            <AnimatedSection delay={0.3}>
              <div className="blog-content-clean mb-12">
                <h2>Gestión de Cookies</h2>
                <p>
                  Usted tiene el derecho de aceptar o rechazar las cookies no esenciales. Puede gestionar sus preferencias 
                  de cookies en cualquier momento de las siguientes maneras:
                </p>
                
                <h3>1. A través de nuestro Banner de Cookies</h3>
                <p>
                  Cuando visite nuestro sitio web por primera vez, verá un banner de cookies que le permite:
                </p>
                <ul>
                  <li><strong>Aceptar todas las cookies:</strong> Permite el uso de todas las cookies</li>
                  <li><strong>Rechazar todas las cookies:</strong> Solo permite las cookies necesarias</li>
                  <li><strong>Configuración personalizada:</strong> Elija qué tipos de cookies aceptar</li>
                </ul>

                <div className="mt-6 p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="mb-4 font-semibold text-blue-900">
                    ¿Desea cambiar sus preferencias de cookies ahora?
                  </p>
                  <Button
                    onClick={openSettings}
                    className="bg-zwheel-black hover:bg-zwheel-gray-800 text-white"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Abrir Configuración de Cookies
                  </Button>
                </div>

                <h3>2. A través de su Navegador</h3>
                <p>
                  También puede gestionar las cookies directamente a través de la configuración de su navegador. 
                  La mayoría de los navegadores le permiten:
                </p>
                <ul>
                  <li>Ver qué cookies tiene almacenadas y eliminarlas individualmente</li>
                  <li>Bloquear cookies de terceros</li>
                  <li>Bloquear todas las cookies de sitios específicos</li>
                  <li>Eliminar todas las cookies cuando cierra el navegador</li>
                </ul>
                <p className="mt-4">
                  <strong>Nota:</strong> Si bloquea todas las cookies, algunas funciones del sitio web pueden no funcionar 
                  correctamente.
                </p>

                <h3>3. Enlaces a Configuraciones de Navegadores Comunes</h3>
                <ul>
                  <li>
                    <a 
                      href="https://support.google.com/chrome/answer/95647" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Google Chrome
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Mozilla Firefox
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Safari
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Microsoft Edge
                    </a>
                  </li>
                </ul>
              </div>
            </AnimatedSection>

            {/* Cookies de Terceros */}
            <AnimatedSection delay={0.4}>
              <div className="blog-content-clean mb-12">
                <h2>Cookies de Terceros</h2>
                <p>
                  Algunas cookies en nuestro sitio web son establecidas por servicios de terceros. Estas cookies pueden 
                  incluir:
                </p>
                <ul>
                  <li><strong>Google Analytics:</strong> Para analizar el uso del sitio web</li>
                  <li><strong>Redes sociales:</strong> Para compartir contenido en plataformas sociales</li>
                  <li><strong>Servicios de publicidad:</strong> Para mostrar anuncios relevantes</li>
                </ul>
                <p className="mt-4">
                  Estas cookies están sujetas a las políticas de privacidad de los respectivos terceros. Le recomendamos 
                  que revise las políticas de privacidad de estos servicios para obtener más información.
                </p>
              </div>
            </AnimatedSection>

            {/* Sus Derechos */}
            <AnimatedSection delay={0.5}>
              <div className="blog-content-clean mb-12">
                <h2>Sus Derechos</h2>
                <p>
                  De acuerdo con el Reglamento General de Protección de Datos (RGPD) y la Ley de Servicios de la Sociedad 
                  de la Información (LSSI), usted tiene los siguientes derechos:
                </p>
                <ul>
                  <li><strong>Derecho a la información:</strong> Tiene derecho a ser informado sobre el uso de cookies</li>
                  <li><strong>Derecho de acceso:</strong> Puede solicitar información sobre qué cookies utilizamos</li>
                  <li><strong>Derecho de oposición:</strong> Puede rechazar el uso de cookies no esenciales</li>
                  <li><strong>Derecho de retirada:</strong> Puede retirar su consentimiento en cualquier momento</li>
                  <li><strong>Derecho de eliminación:</strong> Puede eliminar las cookies almacenadas en su dispositivo</li>
                </ul>
                <p className="mt-4">
                  Para ejercer estos derechos, puede contactarnos a través de{' '}
                  <a 
                    href="mailto:info@zwheel.com" 
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    info@zwheel.com
                  </a>
                  {' '}o utilizar la configuración de cookies en nuestro sitio web.
                </p>
              </div>
            </AnimatedSection>

            {/* Cambios en la Política */}
            <AnimatedSection delay={0.6}>
              <div className="blog-content-clean mb-12">
                <h2>Cambios en esta Política</h2>
                <p>
                  Podemos actualizar esta Política de Cookies de vez en cuando para reflejar cambios en nuestras prácticas 
                  o por otras razones operativas, legales o regulatorias. Le notificaremos cualquier cambio material publicando 
                  la nueva Política de Cookies en esta página y actualizando la fecha de "Última actualización".
                </p>
                <p className="mt-4">
                  Le recomendamos que revise esta página periódicamente para mantenerse informado sobre nuestro uso de cookies.
                </p>
              </div>
            </AnimatedSection>

            {/* Contacto */}
            <AnimatedSection delay={0.7}>
              <div className="blog-content-clean mb-12">
                <h2>Contacto</h2>
                <p>
                  Si tiene preguntas sobre esta Política de Cookies o sobre cómo utilizamos las cookies, puede contactarnos:
                </p>
                <div className="mt-4 p-6 bg-zwheel-gray-50 rounded-xl">
                  <p className="mb-2">
                    <strong>ZWHEEL Technology</strong>
                  </p>
                  <p className="mb-2">
                    Calle Riera Roja 12, 08830<br />
                    Sant Boi de Llobregat, Barcelona, España
                  </p>
                  <p className="mb-2">
                    Email: <a 
                      href="mailto:info@zwheel.com" 
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      info@zwheel.com
                    </a>
                  </p>
                  <p>
                    Teléfono: <a 
                      href="tel:+34936675437" 
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      +34 936 675 437
                    </a>
                  </p>
                </div>
              </div>
            </AnimatedSection>

            {/* Enlaces Relacionados */}
            <AnimatedSection delay={0.8}>
              <div className="mt-12 p-6 bg-zwheel-gray-50 rounded-xl border border-gray-200">
                <h3 className="text-lg font-semibold text-zwheel-black mb-4">
                  Documentos Relacionados
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link 
                      to="/legal/privacy" 
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Política de Privacidad
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/legal/terms" 
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Aviso legal y condiciones de uso
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/contacto" 
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Contacto
                    </Link>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
